import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ChefAvatar } from "../../Components/Chef";
import DishImage from "../../Components/DishImage";
import Price from "../../Components/Price";
import { BackIcon, ForwardIcon } from "../../Components/Icons";
import useMenuData from "../../hooks/useMenuData";
import useTableBill from "../../hooks/useTableBill";
import useOpenDish from "../../hooks/useOpenDish";
import { useAppContext } from "../../context/AppContext";
import { askAssistant } from "../../services/ApiCalls";

const MAX_LENGTH = 500;
const storageKey = (restaurantId) => `opm:assistant:${restaurantId || "_"}`;

// The conversation lives in this tab only, per restaurant: it starts over in a new visit.
const loadHistory = (restaurantId) => {
  try {
    const raw = sessionStorage.getItem(storageKey(restaurantId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};
const saveHistory = (restaurantId, messages) => {
  try {
    sessionStorage.setItem(storageKey(restaurantId), JSON.stringify(messages));
  } catch {
    /* private mode or full storage: the chat still works for this visit */
  }
};

const ChefBubble = ({ children }) => (
  <p className="m-0 self-start max-w-full bg-secondary text-dark text-base leading-[23px] px-4 py-3" style={{ borderRadius: "6px 18px 18px 18px" }}>
    {children}
  </p>
);

const Index = ({ orderList, setCookingLevel, setRemoveIngredients, setSelectedItem }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { guestRestaurantId, currencySymbol, isAuthenticated, restaurantName, tableLabel } = useAppContext();
  const menuData = useMenuData();
  const { bill } = useTableBill();
  const openDish = useOpenDish({ orderList, setCookingLevel, setRemoveIngredients, setSelectedItem });
  const [messages, setMessages] = useState(() => loadHistory(guestRestaurantId));
  const [draft, setDraft] = useState("");
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef(null);
  const rootRef = useRef(null);
  // iOS shrinks only the visual viewport when the keyboard opens, and may also pan it. Pin
  // the screen to exactly the visible area so the messages stay scrollable and the input
  // sits right above the keyboard.
  const [viewport, setViewport] = useState(null);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const sync = () => {
      setViewport(window.innerHeight - vv.height > 120 ? { top: vv.offsetTop, height: vv.height } : null);
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    };
    vv.addEventListener("resize", sync);
    vv.addEventListener("scroll", sync);
    return () => {
      vv.removeEventListener("resize", sync);
      vv.removeEventListener("scroll", sync);
    };
  }, []);

  // With the keyboard open, a drag that the messages cannot take (outside them, or past
  // their top or bottom) would pan the whole page in iOS Chrome: swallow it.
  const keyboardOpen = viewport != null;
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !keyboardOpen) return undefined;
    let lastY = 0;
    const onStart = (e) => {
      lastY = e.touches[0]?.clientY ?? 0;
    };
    const onMove = (e) => {
      const y = e.touches[0]?.clientY ?? lastY;
      const dy = y - lastY;
      lastY = y;
      const list = scrollRef.current;
      if (!list || !list.contains(e.target)) return e.preventDefault();
      const atTop = list.scrollTop <= 0;
      const atBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;
      if ((atTop && dy > 0) || (atBottom && dy < 0)) e.preventDefault();
    };
    root.addEventListener("touchstart", onStart, { passive: true });
    root.addEventListener("touchmove", onMove, { passive: false });
    return () => {
      root.removeEventListener("touchstart", onStart);
      root.removeEventListener("touchmove", onMove);
    };
  }, [keyboardOpen]);

  const items = useMemo(
    () => (menuData?.categories || []).flatMap((cat) => cat?.items || []),
    [menuData]
  );
  // The order example names a dish this restaurant actually has: its most ordered, else its first.
  const featured = useMemo(
    () =>
      items.find((i) => (i?.productInfo?.features || []).some((f) => /הכי מוזמן|popular/i.test(f))) ||
      items[0],
    [items]
  );
  const starters = [
    ...(featured ? [t("chefStarterOrder", { dish: featured.translations?.name })] : []),
    t("chefStarterPay"),
    t("chefStarter1"),
    t("chefStarter2"),
  ];
  const asked = new Set(messages.filter((m) => m.role === "guest").map((m) => m.text));
  const followups = starters.filter((s) => !asked.has(s));
  const started = messages.length > 0;

  useEffect(() => saveHistory(guestRestaurantId, messages), [guestRestaurantId, messages]);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, typing]);

  // Dishes are kept by menu item id; resolve them against the current menu.
  const dishById = useMemo(() => {
    const map = new Map();
    items.forEach((i) => map.set(String(i.menu_item_id), i));
    return map;
  }, [items]);

  const ask = async (text) => {
    const question = text.trim().slice(0, MAX_LENGTH);
    if (!question || typing) return;
    setDraft("");
    const history = [...messages, { role: "guest", text: question }];
    setMessages(history);
    setTyping(true);
    let chef;
    try {
      const reply = await askAssistant({
        messages: history.map(({ role, text: body }) => ({ role, text: body })),
        items: items.map((i) => ({
          id: i.menu_item_id,
          name: i.translations?.name,
          price: i.price,
          description: i.translations?.description,
          features: i.productInfo?.features,
          ingredients: i.productInfo?.ingredients,
          allergens: i.productInfo?.allergens,
        })),
        restaurantName,
        currency: currencySymbol,
        diners: Number(bill?.number_of_people) || 0,
      });
      chef = {
        role: "chef",
        text: reply.text,
        dishIds: reply.dish_ids || [],
        action: reply.action === "pay" ? "pay" : undefined,
      };
    } catch {
      chef = { role: "chef", text: t("chefError"), dishIds: [] };
    }
    setTyping(false);
    setMessages((prev) => [...prev, chef]);
  };

  return (
    <div
      ref={rootRef}
      className={`flex flex-col bg-background ${viewport ? "fixed inset-x-0" : "h-dvh"}`}
      style={viewport ? { top: viewport.top, height: viewport.height } : undefined}
    >
      <header className="flex items-center gap-3 px-4 py-3 border-b border-border">
        <button
          type="button"
          onClick={() => navigate("/")}
          aria-label={t("Back to Menu")}
          className="w-11 h-11 flex-none rounded-full bg-secondary flex items-center justify-center text-dark"
        >
          <BackIcon size={20} />
        </button>
        <ChefAvatar size={44} />
        <div className="flex-1 min-w-0 flex flex-col">
          <h1 className="text-base font-bold text-dark truncate">
            {t("chefName", { restaurant: restaurantName })}
          </h1>
          <span className="text-xs text-muted flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-success" aria-hidden="true" />
            {typing ? t("chefTyping") : t("chefStatus", { table: tableLabel })}
          </span>
        </div>
      </header>

      <main ref={scrollRef} aria-live="polite" className="flex-1 min-h-0 overflow-y-auto overscroll-contain scrollbar-hide px-5 py-6 flex flex-col gap-5">
        <span className="self-center text-xs text-subtle">{t("Today")}</span>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-end gap-2">
            <ChefAvatar size={30} />
            <div className="flex flex-col gap-1.5 max-w-[82%]">
              <ChefBubble>{t("chefGreeting1", { restaurant: restaurantName })}</ChefBubble>
              <ChefBubble>{t("chefGreeting2")}</ChefBubble>
            </div>
          </div>
          <span className="text-xs text-subtle ps-[38px]">{t("chefNow")}</span>
        </div>

        {!started && (
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium text-muted pt-4">{t("chefExamples")}</span>
            <div className="flex flex-col items-end gap-2">
              {starters.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => ask(s)}
                  className="px-4 py-2.5 border-[1.5px] border-primary bg-surface text-primary text-sm font-medium"
                  style={{ borderRadius: "18px 18px 6px 18px" }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) =>
          m.role === "guest" ? (
            <div
              key={i}
              className="self-end max-w-[78%] bg-primary text-white text-base font-medium px-4 py-3 animate-slide-up"
              style={{ borderRadius: "22px 22px 22px 6px" }}
            >
              {m.text}
            </div>
          ) : (
            <div key={i} className="flex gap-2 items-start animate-slide-up">
              <ChefAvatar size={30} />
              <div className="flex-1 min-w-0 flex flex-col gap-2">
                <ChefBubble>{m.text}</ChefBubble>
                {m.action === "pay" && (
                  <button
                    type="button"
                    onClick={() => navigate(isAuthenticated ? "/payment" : "/current-order")}
                    className="self-start h-11 px-5 rounded-md bg-cocoa text-white text-sm font-bold shadow-cocoa"
                  >
                    {t("chefGoToPayment")}
                  </button>
                )}
                {(m.dishIds || []).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {m.dishIds.map((id) => {
                      const dish = dishById.get(String(id));
                      if (!dish) return null;
                      const name = dish.translations?.name;
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => openDish(dish)}
                          className="h-14 ps-3.5 pe-2 rounded-full bg-surface border border-border shadow-soft flex items-center gap-2.5 text-start"
                        >
                          <DishImage src={dish.image_url} alt="" seed={id ?? name} size={40} />
                          <span className="flex flex-col">
                            <span className="text-sm font-bold text-dark leading-[18px]">{name}</span>
                            <Price value={dish.price} className="text-xs font-bold text-primary" />
                          </span>
                          <ForwardIcon size={16} className="text-subtle" />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )
        )}

        {typing && (
          <div role="status" aria-label={t("chefTyping")} className="flex gap-2 items-start">
            <ChefAvatar size={30} />
            <div className="h-11 px-4 flex items-center gap-1.5 bg-secondary" style={{ borderRadius: "6px 18px 18px 18px" }}>
              <span className="typing-dot w-2 h-2 rounded-full bg-primary" />
              <span className="typing-dot w-2 h-2 rounded-full bg-primary" style={{ animationDelay: ".15s" }} />
              <span className="typing-dot w-2 h-2 rounded-full bg-primary" style={{ animationDelay: ".3s" }} />
            </div>
          </div>
        )}
      </main>

      <div className="flex-none bg-background shadow-sheet px-4 pt-3 flex flex-col gap-2.5" style={{ paddingBottom: "calc(24px + env(safe-area-inset-bottom, 0px))" }}>
        {started && followups.length > 0 && (
          <div className="flex gap-2 overflow-x-auto scrollbar-hide">
            {followups.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => ask(f)}
                disabled={typing}
                className="flex-none h-10 px-3.5 rounded-full border-[1.5px] border-primary bg-surface text-primary text-sm font-medium disabled:opacity-50"
              >
                {f}
              </button>
            ))}
          </div>
        )}
        {!started && <p className="m-0 text-xs text-muted text-center">{t("chefDisclaimer")}</p>}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            ask(draft);
          }}
          className="h-[58px] rounded-full bg-surface border border-secondary-dark shadow-soft flex items-center gap-2.5 ps-5 pe-[7px]"
        >
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={MAX_LENGTH}
            placeholder={started ? t("chefPlaceholderMore") : t("chefPlaceholder")}
            aria-label={t("chefPlaceholder")}
            className="flex-1 min-w-0 bg-transparent text-base text-dark placeholder:text-subtle focus:outline-none focus-visible:ring-0"
          />
          <button
            type="submit"
            disabled={!draft.trim() || typing}
            aria-label={t("Send")}
            className="w-11 h-11 flex-none rounded-full bg-primary text-white flex items-center justify-center disabled:opacity-40"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 19V5M6 11l6-6 6 6" />
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
};

export default Index;
